import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtMXNFull, fmtDate, EXPENSE_CATEGORIES, EXPENSE_STATUS } from "@/lib/api";
import IncontiLogo from "@/components/IncontiLogo";
import BitacoraPanel from "@/components/BitacoraPanel";
import ClientPicker from "@/components/ClientPicker";
import { useAuth } from "@/context/AuthContext";
import { Printer, ArrowLeft, Download, Plus, TrendingUp, Trash2, X, FileText } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABEL = Object.fromEntries(EXPENSE_CATEGORIES.map((c) => [c.value, c.label]));
const STATUS_LABEL = Object.fromEntries(EXPENSE_STATUS.map((s) => [s.value, s.label]));
const INCOME_SOURCES = [
  { value: "anticipo", label: "Anticipo" },
  { value: "estimacion", label: "Estimación" },
  { value: "finiquito", label: "Finiquito" },
  { value: "extra", label: "Trabajo extra" },
  { value: "otro", label: "Otro" },
];
const PAYMENT_METHODS = [
  { value: "transferencia", label: "Transferencia" },
  { value: "cheque", label: "Cheque" },
  { value: "deposito", label: "Depósito" },
  { value: "efectivo", label: "Efectivo" },
  { value: "otro", label: "Otro" },
];
const SOURCE_LABEL = Object.fromEntries(INCOME_SOURCES.map((s) => [s.value, s.label]));

export default function ProjectReport() {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [openIncome, setOpenIncome] = useState(false);
  const [openBitacora, setOpenBitacora] = useState(false);
  const [curveData, setCurveData] = useState(null);

  const fetchReport = async () => {
    try {
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const res = await api.get(`/reports/project/${id}`, { params });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo cargar el reporte");
    }
  };

  const fetchCurveS = async () => {
    try {
      const res = await api.get(`/reports/project/${id}/curve-s`);
      setCurveData(res.data);
    } catch {
      setCurveData(null);
    }
  };

  useEffect(() => { fetchReport(); fetchCurveS(); /* eslint-disable-next-line */ }, [id]);

  const downloadExcel = async () => {
    const params = {};
    if (start) params.start_date = start;
    if (end) params.end_date = end;
    const res = await api.get(`/reports/project/${id}/excel`, { params, responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `reporte_${data?.project?.code || "proyecto"}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-300">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400 label-mono">Cargando…</div>;

  const { project, summary, expenses, payroll, incomes = [], investors = [], investor_movements: investorMovements = [] } = data;
  const folio = `RPT-${project.id.slice(0, 8).toUpperCase()}`;
  const utilizationPct = summary.budget > 0 ? Math.min(100, Math.round((summary.total_committed / summary.budget) * 100)) : 0;

  return (
    <div className="min-h-screen" style={{ background: "#0a0f1d" }}>
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .report-page { box-shadow: none !important; margin: 0 !important; max-width: 100% !important; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 px-6 py-3 flex items-center justify-between flex-wrap gap-3"
        style={{ borderBottom: "1px solid var(--border)", background: "rgba(5,8,18,0.9)", backdropFilter: "blur(8px)" }}>
        <button data-testid="report-back-btn" onClick={() => window.history.back()} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" data-testid="report-start" value={start} onChange={(e) => setStart(e.target.value)}
            className="input-cyber px-2 py-1 text-xs" />
          <span className="text-slate-500 text-xs">→</span>
          <input type="date" data-testid="report-end" value={end} onChange={(e) => setEnd(e.target.value)}
            className="input-cyber px-2 py-1 text-xs" />
          <button data-testid="report-apply-btn" onClick={fetchReport} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm">
            Aplicar
          </button>
          <button data-testid="report-bitacora-btn" onClick={() => setOpenBitacora(true)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
            style={{ background: "var(--magenta)", color: "white", boxShadow: "0 0 12px rgba(217,70,239,0.4)" }}>
            <FileText className="w-4 h-4" /> Bitácora
          </button>
          <a href={`/projects/${id}/budget`} data-testid="report-budget-btn" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2 no-underline"
            style={{ background: "#7c3aed", color: "white", boxShadow: "0 0 12px rgba(124,58,237,0.4)" }}>
            <FileText className="w-4 h-4" /> Presupuesto
          </a>
          <a href={`/projects/${id}/expenses`} data-testid="report-expenses-btn" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2 no-underline"
            style={{ background: "#dc2626", color: "white", boxShadow: "0 0 12px rgba(220,38,38,0.4)" }}>
            <Plus className="w-4 h-4" /> Gastos
          </a>
          <button data-testid="report-income-btn" onClick={() => setOpenIncome(true)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
            style={{ background: "var(--green)", color: "#0c1424", boxShadow: "0 0 12px rgba(34,197,94,0.4)" }}>
            <Plus className="w-4 h-4" /> Ingreso
          </button>
          <button data-testid="report-excel-btn" onClick={downloadExcel} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
            style={{ borderColor: "var(--magenta)", color: "var(--magenta)" }}>
            <Download className="w-4 h-4" /> Excel
          </button>
          <button data-testid="report-print-btn" onClick={() => window.print()} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2">
            <Printer className="w-4 h-4" /> Imprimir / PDF
          </button>
        </div>
      </div>

      <div className="py-8 px-4">
        <div className="report-page mx-auto bg-white text-slate-900 max-w-[920px] shadow-2xl">
          {/* Letterhead */}
          <div className="px-10 pt-10 pb-6 border-b-4" style={{ borderColor: "#3F2A91" }}>
            <div className="flex items-start justify-between">
              <div>
                <IncontiLogo size={56} />
                <div className="mt-3 text-xs text-slate-500 leading-snug">
                  CORPORATION INCONTI · administracion@inconti.net
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">// FOLIO</div>
                <div className="font-mono text-lg font-bold text-slate-900">{folio}</div>
                <div className="mt-2 text-[11px] uppercase tracking-widest text-slate-400 font-mono">// EMITIDO</div>
                <div className="font-mono text-xs text-slate-700">{fmtDate(new Date().toISOString())}</div>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="px-10 py-6 text-center">
            <h1 className="text-3xl font-display font-black tracking-widest" style={{ color: "#3F2A91" }}>
              REPORTE FINANCIERO DE PROYECTO
            </h1>
            {(start || end) && (
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1 font-mono">
                Período: {start || "inicio"} → {end || "actual"}
              </div>
            )}
          </div>

          {/* Project meta */}
          <div className="grid grid-cols-2 gap-6 px-10 mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">PROYECTO</div>
              <div className="font-bold text-lg text-slate-900">{project.code} · {project.name}</div>
              <div className="text-sm text-slate-600">{project.client}</div>
              <div className="text-xs text-slate-500">{project.address}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">ESTADO</div>
              <div className="font-bold text-base text-slate-900 uppercase">{project.status}</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-2 rounded" style={{ background: "#e5e7eb" }}>
                  <div className="h-full rounded" style={{ width: `${project.progress}%`, background: "#3F2A91" }} />
                </div>
                <span className="font-mono text-xs text-slate-700">{project.progress}%</span>
              </div>
            </div>
          </div>

          {/* KPIs */}
          <div className="px-10 mb-6">
            <div className="grid grid-cols-4 gap-3">
              <Kpi label="Presupuesto" value={fmtMXNFull(summary.budget)} color="#0f172a" />
              <Kpi label="Ingresos cobrados" value={fmtMXNFull(summary.total_income || 0)} color="#16a34a" />
              <Kpi label="Pagado a proveedores" value={fmtMXNFull(summary.total_paid)} color="#dc2626" />
              <Kpi label="Balance neto" value={fmtMXNFull(summary.net_balance || 0)} color={(summary.net_balance || 0) >= 0 ? "#16a34a" : "#dc2626"} />
            </div>
            <div className="grid grid-cols-4 gap-3 mt-3">
              <Kpi label="Comprometido" value={fmtMXNFull(summary.total_committed)} color="#3F2A91" />
              <Kpi label="Pendiente aprobar" value={fmtMXNFull(summary.total_pending)} color="#d97706" />
              <Kpi label="Nómina pagada" value={fmtMXNFull(summary.payroll_paid || 0)} color="#0891b2" />
              <Kpi label="Saldo vs presupuesto" value={fmtMXNFull(summary.remaining)} color={summary.remaining >= 0 ? "#16a34a" : "#dc2626"} />
            </div>
            {(summary.investor_aportado > 0 || summary.investor_count > 0) && (
              <div className="grid grid-cols-4 gap-3 mt-3">
                <Kpi label={`Capital de ${summary.investor_count} inversionista${summary.investor_count === 1 ? "" : "s"}`}
                  value={fmtMXNFull(summary.investor_aportado || 0)} color="#7c3aed" />
                <Kpi label="Distribuido a inversionistas" value={fmtMXNFull(summary.investor_distribuido || 0)} color="#d946ef" />
                <Kpi label="Intereses pagados" value={fmtMXNFull(summary.investor_interes || 0)} color="#d97706" />
                <Kpi
                  label="% de obra financiado por inversionistas"
                  value={summary.budget > 0 ? `${Math.min(999, ((summary.investor_aportado || 0) / summary.budget * 100)).toFixed(1)}%` : "—"}
                  color="#7c3aed"
                />
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="px-3 py-2 text-xs" style={{ background: "#f8fafc", border: "1px solid #e5e7eb" }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-700">Utilización del presupuesto</span>
                  <span className="font-mono font-bold tabular-nums" style={{ color: utilizationPct > 90 ? "#dc2626" : utilizationPct > 75 ? "#d97706" : "#3F2A91" }}>{utilizationPct}%</span>
                </div>
                <div className="h-2 rounded" style={{ background: "#e5e7eb" }}>
                  <div className="h-full rounded" style={{ width: `${utilizationPct}%`, background: utilizationPct > 90 ? "#dc2626" : utilizationPct > 75 ? "#d97706" : "#3F2A91" }} />
                </div>
              </div>
              <div className="px-3 py-2 text-xs" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-700">Cobranza vs presupuesto</span>
                  <span className="font-mono font-bold tabular-nums" style={{ color: "#16a34a" }}>{Math.min(999, summary.collection_pct || 0)}%</span>
                </div>
                <div className="h-2 rounded" style={{ background: "#dcfce7" }}>
                  <div className="h-full rounded" style={{ width: `${Math.min(100, summary.collection_pct || 0)}%`, background: "#16a34a" }} />
                </div>
              </div>
            </div>
          </div>

          {/* Curva S - Avance físico vs financiero */}
          {curveData && curveData.series && curveData.series.length > 0 && (
            <div className="px-10 mb-6">
              <CurveSChart data={curveData} />
            </div>
          )}

          {/* Ingresos detail */}
          <div className="px-10 mb-6">
            <h2 className="text-sm font-bold uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: "#16a34a" }}>
              <TrendingUp className="w-4 h-4" /> Ingresos del proyecto ({incomes.length})
            </h2>
            {incomes.length === 0 ? (
              <div className="text-sm text-slate-500 italic px-3 py-4 text-center" style={{ background: "#f8fafc" }}>
                Sin ingresos registrados. Usa el botón "+ INGRESO" arriba para registrar pagos del cliente.
              </div>
            ) : (
              <table className="w-full text-xs border" style={{ borderColor: "#cbd5e1" }} data-testid="income-table">
                <thead>
                  <tr style={{ background: "#16a34a", color: "white" }}>
                    <th className="text-left px-2 py-1.5 font-semibold">Fecha</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Concepto</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Descripción</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Método</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Referencia</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Monto</th>
                    <th className="no-print text-right px-2 py-1.5 font-semibold">·</th>
                  </tr>
                </thead>
                <tbody>
                  {incomes.map((i) => (
                    <tr key={i.id} data-testid={`income-row-${i.id}`} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                      <td className="px-2 py-1.5 font-mono">{fmtDate(i.income_date)}</td>
                      <td className="px-2 py-1.5 font-semibold uppercase" style={{ color: "#16a34a" }}>{SOURCE_LABEL[i.source] || i.source}</td>
                      <td className="px-2 py-1.5">{i.description || "—"}</td>
                      <td className="px-2 py-1.5 capitalize text-slate-600">{i.payment_method}</td>
                      <td className="px-2 py-1.5 font-mono text-slate-600">{i.reference || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums font-bold" style={{ color: "#16a34a" }}>+{fmtMXNFull(i.amount)}</td>
                      <td className="no-print px-2 py-1.5 text-right">
                        <button data-testid={`income-delete-${i.id}`} onClick={async () => {
                          if (!window.confirm("¿Eliminar ingreso?")) return;
                          try { await api.delete(`/incomes/${i.id}`); toast.success("Eliminado"); fetchReport(); }
                          catch (e) { toast.error(e.response?.data?.detail || "Error"); }
                        }} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: "#f0fdf4", borderTop: "2px solid #16a34a" }}>
                    <td colSpan={5} className="px-2 py-2 text-right font-bold uppercase tracking-widest text-[11px]">TOTAL INGRESOS</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-black text-base" style={{ color: "#16a34a" }}>{fmtMXNFull(summary.total_income || 0)}</td>
                    <td className="no-print" />
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* Anexo de inversionistas */}
          {(investors.length > 0 || investorMovements.length > 0) && (
            <div className="px-10 mb-6">
              <h2 className="text-sm font-bold uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: "#7c3aed" }}>
                <TrendingUp className="w-4 h-4" /> Anexo · Capital de inversionistas ({investors.length})
              </h2>
              <table className="w-full text-xs border mb-3" style={{ borderColor: "#cbd5e1" }} data-testid="investors-table">
                <thead>
                  <tr style={{ background: "#7c3aed", color: "white" }}>
                    <th className="text-left px-2 py-1.5 font-semibold">Inversionista</th>
                    <th className="text-left px-2 py-1.5 font-semibold">RFC</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Rend. mes</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Aportado</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Distribuido</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Intereses</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Movimientos</th>
                  </tr>
                </thead>
                <tbody>
                  {investors.map((inv) => (
                    <tr key={inv.investor_id} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                      <td className="px-2 py-1.5 font-semibold">{inv.name}</td>
                      <td className="px-2 py-1.5 font-mono text-slate-500">{inv.rfc || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums" style={{ color: "#7c3aed" }}>{(inv.monthly_yield_pct || 0).toFixed(2)}%</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums" style={{ color: "#16a34a" }}>{fmtMXNFull(inv.aportado || 0)}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums" style={{ color: "#d946ef" }}>{fmtMXNFull(inv.distribuido || 0)}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums" style={{ color: "#d97706" }}>{fmtMXNFull(inv.interes || 0)}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-500">{inv.movement_count}</td>
                    </tr>
                  ))}
                  <tr style={{ background: "#f5f3ff", borderTop: "2px solid #7c3aed" }}>
                    <td className="px-2 py-2 font-bold uppercase tracking-widest text-[11px]">TOTAL</td>
                    <td colSpan={2} />
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-black text-base" style={{ color: "#16a34a" }}>{fmtMXNFull(summary.investor_aportado || 0)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-black text-base" style={{ color: "#d946ef" }}>{fmtMXNFull(summary.investor_distribuido || 0)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums font-black text-base" style={{ color: "#d97706" }}>{fmtMXNFull(summary.investor_interes || 0)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-500">{investorMovements.length}</td>
                  </tr>
                </tbody>
              </table>
              {investorMovements.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600 hover:text-slate-900 font-semibold">Ver detalle de movimientos ({investorMovements.length})</summary>
                  <table className="w-full text-xs border mt-2" style={{ borderColor: "#cbd5e1" }}>
                    <thead>
                      <tr style={{ background: "#f5f3ff" }}>
                        <th className="text-left px-2 py-1.5 font-semibold">Fecha</th>
                        <th className="text-left px-2 py-1.5 font-semibold">Tipo</th>
                        <th className="text-left px-2 py-1.5 font-semibold">Referencia</th>
                        <th className="text-left px-2 py-1.5 font-semibold">Método</th>
                        <th className="text-right px-2 py-1.5 font-semibold">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {investorMovements.map((m) => {
                        const sign = m.movement_type === "aportacion" ? "+" : "−";
                        const color = m.movement_type === "aportacion" ? "#16a34a"
                          : m.movement_type === "distribucion" ? "#d946ef" : "#d97706";
                        return (
                          <tr key={m.id} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                            <td className="px-2 py-1 font-mono">{fmtDate(m.movement_date)}</td>
                            <td className="px-2 py-1 capitalize">{(m.movement_type || "").replace("_", " ")}</td>
                            <td className="px-2 py-1 font-mono text-slate-500">{m.reference || "—"}</td>
                            <td className="px-2 py-1 capitalize text-slate-500">{(m.payment_method || "").replace("_", " ")}</td>
                            <td className="px-2 py-1 text-right font-mono tabular-nums font-semibold" style={{ color }}>{sign}{fmtMXNFull(m.amount || 0)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          )}

          {/* Categoría breakdown */}
          {Object.keys(summary.by_category).length > 0 && (
            <div className="px-10 mb-6">
              <h2 className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: "#3F2A91" }}>Gastos por categoría</h2>
              <table className="w-full text-sm border" style={{ borderColor: "#cbd5e1" }}>
                <thead>
                  <tr style={{ background: "#3F2A91", color: "white" }}>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-widest">Categoría</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-widest">Monto</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-widest">% del total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary.by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
                    const pct = summary.total_committed > 0 ? ((amt / summary.total_committed) * 100).toFixed(1) : "0";
                    return (
                      <tr key={cat} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                        <td className="px-3 py-2 capitalize">{CATEGORY_LABEL[cat] || cat}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMXNFull(amt)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-500">{pct}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Expense detail */}
          <div className="px-10 mb-6">
            <h2 className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: "#3F2A91" }}>Detalle de gastos ({expenses.length})</h2>
            {expenses.length === 0 ? (
              <div className="text-sm text-slate-500 italic px-3 py-4 text-center" style={{ background: "#f8fafc" }}>Sin gastos en el período seleccionado.</div>
            ) : (
              <table className="w-full text-xs border" style={{ borderColor: "#cbd5e1" }}>
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th className="text-left px-2 py-1.5 font-semibold">Fecha</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Categoría</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Descripción</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Proveedor</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Monto</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((e) => (
                    <tr key={e.id} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                      <td className="px-2 py-1.5 font-mono">{fmtDate(e.expense_date)}</td>
                      <td className="px-2 py-1.5 capitalize">{CATEGORY_LABEL[e.category] || e.category}</td>
                      <td className="px-2 py-1.5">{e.description}</td>
                      <td className="px-2 py-1.5 text-slate-600">{e.vendor || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums font-semibold">{fmtMXNFull(e.amount)}</td>
                      <td className="px-2 py-1.5 uppercase font-semibold" style={{ color: e.status === "pagado" ? "#0891b2" : e.status === "aprobado" ? "#16a34a" : e.status === "rechazado" ? "#dc2626" : "#d97706" }}>
                        {STATUS_LABEL[e.status] || e.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Payroll detail */}
          {payroll.length > 0 && (
            <div className="px-10 mb-6">
              <h2 className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: "#3F2A91" }}>Nómina del proyecto ({payroll.length})</h2>
              <table className="w-full text-xs border" style={{ borderColor: "#cbd5e1" }}>
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th className="text-left px-2 py-1.5 font-semibold">Empleado</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Período</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Bruto</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Bonos</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Deducc.</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Neto</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {payroll.map((p) => (
                    <tr key={p.id} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                      <td className="px-2 py-1.5">{p.employee_name}</td>
                      <td className="px-2 py-1.5 font-mono text-slate-600">{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmtMXNFull(p.gross)}</td>
                      <td className="px-2 py-1.5 text-right font-mono" style={{ color: "#16a34a" }}>+{fmtMXNFull(p.bonuses)}</td>
                      <td className="px-2 py-1.5 text-right font-mono" style={{ color: "#dc2626" }}>-{fmtMXNFull(p.deductions)}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold" style={{ color: "#3F2A91" }}>{fmtMXNFull(p.net)}</td>
                      <td className="px-2 py-1.5 uppercase font-semibold">{p.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div className="px-10 py-4 text-center text-[10px] text-slate-400 border-t mt-6" style={{ borderColor: "#e5e7eb" }}>
            Documento generado por Corporation Inconti ERP · {folio} · {fmtDate(new Date().toISOString())}
          </div>
        </div>
      </div>

      {openIncome && (
        <IncomeModal
          projectId={id}
          onClose={() => setOpenIncome(false)}
          onSaved={() => { setOpenIncome(false); fetchReport(); }}
        />
      )}

      {openBitacora && (
        <BitacoraPanel
          projectId={id}
          projectCode={project.code}
          currentUser={currentUser}
          onClose={() => setOpenBitacora(false)}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className="px-3 py-2.5 rounded" style={{ border: "1px solid #cbd5e1", background: "#f8fafc" }}>
      <div className="text-[9px] uppercase tracking-widest font-mono text-slate-500">{label}</div>
      <div className="font-display font-black text-lg tabular-nums mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function IncomeModal({ projectId, onClose, onSaved }) {
  const [form, setForm] = useState({
    project_id: projectId,
    amount: 0,
    income_date: new Date().toISOString().slice(0, 10),
    source: "estimacion",
    reference: "",
    description: "",
    payment_method: "transferencia",
    contracting_entity_id: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (!payload.reference) delete payload.reference;
      if (!payload.description) delete payload.description;
      if (!payload.contracting_entity_id) delete payload.contracting_entity_id;
      await api.post(`/projects/${projectId}/incomes`, payload);
      toast.success("Ingreso registrado");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar ingreso");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div className="w-full max-w-lg overflow-y-auto rounded-md shadow-2xl" style={{ background: "#0a0f1d", border: "1px solid var(--border-2)", maxHeight: "calc(100dvh - 2rem)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 flex items-center justify-between sticky top-0 z-10" style={{ background: "var(--bg-2)", borderBottom: "1px solid var(--border)" }}>
          <div>
            <div className="label-mono" style={{ color: "var(--green)" }}>// INGRESO</div>
            <h2 className="font-display font-bold text-xl text-slate-100 tracking-wide">Registrar pago recibido</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1.5">Monto MXN</label>
              <input type="number" required step="0.01" min="0" value={form.amount} data-testid="income-amount"
                onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Fecha</label>
              <input type="date" required value={form.income_date} data-testid="income-date"
                onChange={(e) => setForm({ ...form, income_date: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1.5">Concepto</label>
              <select value={form.source} data-testid="income-source"
                onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm">
                {INCOME_SOURCES.map((s) => <option key={s.value} value={s.value} style={{ background: "#0a0f1d" }}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Método de pago</label>
              <select value={form.payment_method} data-testid="income-method"
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm">
                {PAYMENT_METHODS.map((s) => <option key={s.value} value={s.value} style={{ background: "#0a0f1d" }}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label-mono block mb-1.5">Cliente que paga (opcional)</label>
            <ClientPicker
              value={form.contracting_entity_id || null}
              onChange={(id) => setForm({ ...form, contracting_entity_id: id || "" })}
              label=""
              placeholder="Vincular ingreso con un cliente…"
              allowEmpty
            />
          </div>
          <div>
            <label className="label-mono block mb-1.5">Referencia (#cheque, #transferencia, folio)</label>
            <input value={form.reference} data-testid="income-reference"
              onChange={(e) => setForm({ ...form, reference: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" placeholder="Opcional" />
          </div>
          <div>
            <label className="label-mono block mb-1.5">Descripción / Notas</label>
            <textarea value={form.description} rows={2} data-testid="income-description"
              onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm"
              placeholder="Ej: Anticipo 10% según contrato firmado el…" />
          </div>
          <div className="flex gap-2 justify-end pt-3 -mx-6 -mb-6 px-6 pb-6 sticky bottom-0 z-20"
            style={{ borderTop: "1px solid var(--border)", background: "#0a0f1d", paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
            <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
            <button type="submit" disabled={submitting} data-testid="income-save"
              className="px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm"
              style={{ background: "var(--green)", color: "#0c1424", boxShadow: "0 0 12px rgba(34,197,94,0.4)" }}>
              {submitting ? "Guardando…" : "Registrar ingreso"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// --- Curva S: Planeado vs Físico vs Financiero ------------------------------
function CurveSChart({ data }) {
  const W = 820;
  const H = 280;
  const PAD = { top: 16, right: 16, bottom: 38, left: 42 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const series = data.series || [];
  const n = series.length;
  if (n === 0) return null;

  // X scale
  const xAt = (i) => PAD.left + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const yAt = (pct) => PAD.top + innerH - (Math.max(0, Math.min(100, pct)) / 100) * innerH;

  const buildPath = (key) => {
    let d = "";
    let started = false;
    for (let i = 0; i < n; i++) {
      const v = series[i][key];
      if (v == null) continue;
      const x = xAt(i);
      const y = yAt(v);
      d += `${started ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)} `;
      started = true;
    }
    return d.trim();
  };

  const plannedPath = buildPath("planned_pct");
  const physicalPath = buildPath("physical_pct");
  const financialPath = buildPath("financial_pct");
  const financialContractPath = buildPath("financial_contract_pct");

  const gridY = [0, 25, 50, 75, 100];
  // Sample at most ~8 month labels to avoid overlap
  const labelStep = Math.max(1, Math.ceil(n / 8));

  // Today reference line (last index with data)
  const todayIso = new Date().toISOString().slice(0, 7);
  let todayIdx = -1;
  for (let i = 0; i < n; i++) {
    if (series[i].month === todayIso) { todayIdx = i; break; }
  }

  return (
    <div data-testid="curve-s-chart">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">// CURVA S · AVANCE FÍSICO VS FINANCIERO</div>
        <div className="text-[10px] text-slate-500 font-mono">
          {data.project.start_date} → {data.project.end_date}
        </div>
      </div>
      <div className="bg-white border" style={{ borderColor: "#e2e8f0" }}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Curva S">
          {/* Y-axis gridlines */}
          {gridY.map((g) => (
            <g key={g}>
              <line x1={PAD.left} y1={yAt(g)} x2={W - PAD.right} y2={yAt(g)} stroke="#e2e8f0" strokeWidth="1" />
              <text x={PAD.left - 6} y={yAt(g) + 3} textAnchor="end" fontSize="10" fill="#64748b">{g}%</text>
            </g>
          ))}
          {/* Today vertical line */}
          {todayIdx >= 0 && (
            <g>
              <line
                x1={xAt(todayIdx)} y1={PAD.top} x2={xAt(todayIdx)} y2={H - PAD.bottom}
                stroke="#94a3b8" strokeWidth="1" strokeDasharray="3 3"
              />
              <text x={xAt(todayIdx) + 4} y={PAD.top + 10} fontSize="9" fill="#475569">hoy</text>
            </g>
          )}
          {/* X-axis labels */}
          {series.map((s, i) => (
            i % labelStep === 0 || i === n - 1 ? (
              <text key={s.month} x={xAt(i)} y={H - PAD.bottom + 16} textAnchor="middle" fontSize="9" fill="#64748b">
                {s.label}
              </text>
            ) : null
          ))}
          {/* Planned line */}
          {plannedPath && (
            <path d={plannedPath} stroke="#94a3b8" strokeWidth="2" strokeDasharray="6 4" fill="none" />
          )}
          {/* Financial vs budget */}
          {financialPath && (
            <path d={financialPath} stroke="#dc2626" strokeWidth="2.5" fill="none" />
          )}
          {/* Financial vs contract */}
          {financialContractPath && (
            <path d={financialContractPath} stroke="#f59e0b" strokeWidth="2.5" strokeDasharray="4 3" fill="none" />
          )}
          {/* Physical line */}
          {physicalPath && (
            <path d={physicalPath} stroke="#16a34a" strokeWidth="2.5" fill="none" />
          )}
          {/* Data points - financial */}
          {series.map((s, i) => (
            s.financial_pct != null ? (
              <circle key={`f-${i}`} cx={xAt(i)} cy={yAt(s.financial_pct)} r="2.5" fill="#dc2626" />
            ) : null
          ))}
          {/* Data points - financial contract */}
          {series.map((s, i) => (
            s.financial_contract_pct != null ? (
              <circle key={`fc-${i}`} cx={xAt(i)} cy={yAt(s.financial_contract_pct)} r="2.5" fill="#f59e0b" />
            ) : null
          ))}
          {/* Data points - physical */}
          {series.map((s, i) => (
            s.physical_pct != null ? (
              <circle key={`p-${i}`} cx={xAt(i)} cy={yAt(s.physical_pct)} r="2.5" fill="#16a34a" />
            ) : null
          ))}
          {/* Axis baseline */}
          <line x1={PAD.left} y1={H - PAD.bottom} x2={W - PAD.right} y2={H - PAD.bottom} stroke="#94a3b8" strokeWidth="1" />
        </svg>
        <div className="flex items-center justify-center gap-5 py-2 text-[11px] text-slate-600 border-t flex-wrap" style={{ borderColor: "#e2e8f0" }}>
          <span className="flex items-center gap-1.5"><span style={{ display: "inline-block", width: 18, height: 2, background: "#94a3b8", borderTop: "2px dashed #94a3b8" }} /> Planeado</span>
          <span className="flex items-center gap-1.5"><span style={{ display: "inline-block", width: 18, height: 2.5, background: "#16a34a" }} /> Físico real</span>
          <span className="flex items-center gap-1.5"><span style={{ display: "inline-block", width: 18, height: 2.5, background: "#dc2626" }} /> Financiero vs presupuesto</span>
          {data.has_contract && (
            <span className="flex items-center gap-1.5"><span style={{ display: "inline-block", width: 18, height: 2.5, background: "#f59e0b", borderTop: "2px dashed #f59e0b" }} /> Financiero vs contrato</span>
          )}
        </div>
      </div>
      {!data.has_physical_history && (
        <div className="text-[10px] text-slate-500 mt-1 italic">
          Para ver el avance físico mes a mes, captura el % de avance en las partidas del presupuesto. Mientras tanto se muestra el avance global actual.
        </div>
      )}
    </div>
  );
}
